import React, { createContext, useContext, useMemo } from 'react';

import en from './locales/en.json';
import id from './locales/id.json';
import ms from './locales/ms.json';
import zh from './locales/zh.json';

const locales = { en, id, ms, zh };

export const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'id', label: 'Bahasa Indonesia' },
  { code: 'ms', label: 'Bahasa Melayu' },
  { code: 'zh', label: '中文 (Mandarin)' },
];

const TranslationContext = createContext(null);

/**
 * Get a nested value from an object using a dot-separated path.
 */
const get = (obj, path) => {
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
};

/**
 * Translation provider. Pass the language code (from content.settings.language).
 * Defaults to 'en' if no lang is provided.
 */
export const TranslationProvider = ({ children, lang = 'en' }) => {
  const messages = locales[lang] || locales.en;
  const fallback = locales.en;

  const value = useMemo(() => {
    /**
     * Translate a key, with optional interpolation.
     * Usage: t('rsvp.fullName') or t('success.thankYouName', { name: 'John' })
     */
    const t = (key, params) => {
      let text = get(messages, key) || get(fallback, key) || key;
      if (params) {
        Object.entries(params).forEach(([k, v]) => {
          text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
        });
      }
      return text;
    };

    return { t, lang, messages };
  }, [messages, fallback, lang]);

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

/**
 * Hook to access translations. Must be used within TranslationProvider.
 * Falls back gracefully if no provider is found (returns English).
 */
export const useTranslation = () => {
  const ctx = useContext(TranslationContext);
  if (ctx) return ctx;

  // Fallback: no provider mounted — return English translations
  const t = (key, params) => {
    let text = get(en, key) || key;
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
      });
    }
    return text;
  };
  return { t, lang: 'en', messages: en };
};
